import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import { AdapterService } from '../services/adapter.service';
import { SquareDeviceService } from '../services/square-device.service';
import * as SquareDeviceActions from './square-device.actions';

@Injectable()
export class SquareDeviceEffects {
    private _actions$ = inject(Actions);
    private _squareDeviceService = inject(SquareDeviceService);
    private _alertService = inject(AlertService);
    private _adapter = inject(AdapterService);

    messages = {
        getAllError: 'Error getting all square devices',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All
    getAllSquareDevices$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareDeviceActions.loadSquareDevices),
            switchMap((action) =>
                this._squareDeviceService.getAll(action.params).pipe(
                    map((res) => {
                        const data = this._adapter.adaptAll(res.data);
                        return SquareDeviceActions.loadSquareDevicesSuccess({
                            squareDevices: data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            SquareDeviceActions.loadSquareDevicesFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    // Get One
    getSquareDevice$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareDeviceActions.loadSquareDevice),
            switchMap((action) =>
                this._squareDeviceService.getOne(action.id).pipe(
                    map((res) =>
                        SquareDeviceActions.loadSquareDeviceSuccess({
                            squareDevice: res.data,
                        })
                    ),
                    catchError((error) =>
                        of(
                            SquareDeviceActions.loadSquareDeviceFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    // Create
    createSquareDevice$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareDeviceActions.createSquareDevice),
            switchMap((action) =>
                this._squareDeviceService.create(action.data).pipe(
                    map((res) => {
                        const data = this._adapter.adaptSingle(res.data);
                        return SquareDeviceActions.createSquareDeviceSuccess({
                            squareDevice: data,
                            success: 'Square device created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            SquareDeviceActions.createSquareDeviceFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    // Update
    updateSquareDevice$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareDeviceActions.updateSquareDevice),
            switchMap((action) =>
                this._squareDeviceService
                    .update(action.payload, action.id)
                    .pipe(
                        map((res) => {
                            const data = this._adapter.adaptSingle(res.data);
                            return SquareDeviceActions.updateSquareDeviceSuccess(
                                {
                                    squareDevice: data,
                                    success:
                                        'Square device updated successfully',
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                SquareDeviceActions.updateSquareDeviceFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    )
            )
        )
    );

    // Delete
    deleteSquareDevice$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareDeviceActions.deleteSquareDevice),
            switchMap((action) =>
                this._squareDeviceService.delete(action.id).pipe(
                    map(() =>
                        SquareDeviceActions.deleteSquareDeviceSuccess({
                            id: action.id,
                            success: 'Square device deleted successfully',
                        })
                    ),
                    catchError((error) =>
                        of(
                            SquareDeviceActions.deleteSquareDeviceFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    // Get All Unassigned
    getAllUnassignedSquareDevices$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareDeviceActions.loadUnassignedSquareDevices),
            switchMap((action) =>
                this._squareDeviceService.getAllUnassigned(action.params).pipe(
                    map((res) => {
                        console.log('Unassigned devices response:', res);
                        const devices = [
                            ...res.data.standard,
                            ...res.data.terminal_api,
                        ];
                        // const data = this._adapter.adaptAll(devices);
                        return SquareDeviceActions.loadUnassignedSquareDevicesSuccess(
                            {
                                unassignedSquareDevices: devices,
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            SquareDeviceActions.loadUnassignedSquareDevicesFailure(
                                {
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                }
                            )
                        )
                    )
                )
            )
        )
    );

    // -----------------------------------------------------------
    // @ Side Effects (alerts)
    // -----------------------------------------------------------

    onSquareDeviceActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    SquareDeviceActions.createSquareDeviceSuccess,
                    SquareDeviceActions.updateSquareDeviceSuccess,
                    SquareDeviceActions.deleteSquareDeviceSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onSquareDeviceActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    SquareDeviceActions.loadSquareDevicesFailure,
                    SquareDeviceActions.loadSquareDeviceFailure,
                    SquareDeviceActions.createSquareDeviceFailure,
                    SquareDeviceActions.updateSquareDeviceFailure,
                    SquareDeviceActions.deleteSquareDeviceFailure,
                    SquareDeviceActions.loadUnassignedSquareDevicesFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
